import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useActiveTenant } from "../../hooks/useActiveTenant";
import NoTenantSelected from "../../components/NoTenantSelected";
import { fetchMyOpenings, fetchMyTeachingRoles } from "../../api/apiClient";
import { slotActionKind, optimisticPatch, runSlotAction, runSignupBatch, SUCCESS_MESSAGE, friendlySlotError } from "./slotActions";
import { formatSessionTimeRange, parseSessionTopics } from "../studentSessionHelpers";
import OpenClassCard from "./schedule/OpenClassCard";
import ClassDetailsDialog from "./schedule/ClassDetailsDialog";
import { Note, FactLine, ICONS } from "./schedule/scheduleUi";
import RequestRow from "./schedule/RequestRow";
import { groupOpenSlots, groupByCourse, groupRequests, heldByMe, isRequest } from "./schedule/scheduleModel";

// ── Design tokens (Beacon) ───────────────────────────────────────────────────
const T = {
  ink: "var(--color-ink)",
  ink3: "var(--color-ink-3)",
  ink4: "var(--color-ink-4)",
  line: "#E5E7EB",
  line2: "#EFEDF2",
  surface2: "#FAF9FC",
  surface3: "#F4F2F8",
  primary: "var(--color-primary)",
  primary700: "#2A1F47",
  primaryTint: "rgba(21,15,35,.06)",
  green: "#15803D",
  green50: "rgba(21,128,61,.10)",
  amber: "#B5651D",
  amber50: "rgba(181,101,29,.12)",
  danger: "#B42318",
  danger50: "rgba(180,35,24,.08)",
  danger100: "rgba(180,35,24,.18)",
  blue: "#2563EB",
  blue50: "rgba(37,99,235,.10)",
  shadowSm: "0 1px 0 rgba(20,24,40,.04),0 1px 3px rgba(20,24,40,.06)",
  fontDisplay: "var(--font-display)",
};

const ROLE_LABEL = {
  LEAD_INSTRUCTOR: "Lead Instructor",
  ASSISTANT_INSTRUCTOR: "Assistant Instructor",
  TA: "TA",
};
const ROLE_SHORT = { LEAD_INSTRUCTOR: "Lead", ASSISTANT_INSTRUCTOR: "Assist", TA: "TA" };
const ROLE_META = {
  LEAD_INSTRUCTOR: { label: "Lead", fg: "var(--color-primary)", bg: "rgba(21,15,35,.08)" },
  ASSISTANT_INSTRUCTOR: { label: "Assist", fg: T.blue, bg: T.blue50 },
  TA: { label: "TA", fg: T.amber, bg: T.amber50 },
};
const CONFIRM_TITLE = {
  request: "Request this slot?",
  drop: "Drop this slot?",
  signup: "Sign up for this slot?",
  withdraw: "Withdraw your sign-up?",
};
const CONFIRM_BUTTON = { request: "Request slot", drop: "Drop slot", signup: "Sign up", withdraw: "Withdraw sign-up" };
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOWS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// ── Pure helpers (exported for unit tests) ────────────────────────────────────
export const pad2 = (n) => String(n).padStart(2, "0");
export const isoDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
export const parseISO = (s) => {
  const [y, m, d] = String(s).split("-").map(Number);
  return new Date(y, m - 1, d);
};
export const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
export const addDays = (d, n) => {
  const c = new Date(d);
  c.setDate(c.getDate() + n);
  return c;
};
export const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};
export const daysUntil = (iso) => Math.round((parseISO(iso) - startOfToday()) / 86400000);
export const isWithinTwoDays = (iso) => daysUntil(iso) < 2;
// The server computes these on its own clock (the one it enforces the drop rule with); the local-time
// versions above are only a fallback for responses that don't carry them.
const isDropLocked = (o) => (o.dropLocked != null ? o.dropLocked : isWithinTwoDays(o.sessionDate));
const daysToSession = (o) => (o.daysUntil != null ? o.daysUntil : daysUntil(o.sessionDate));
export const sameMonth = (iso, ref) => {
  const d = parseISO(iso);
  return d.getFullYear() === ref.getFullYear() && d.getMonth() === ref.getMonth();
};

export const minutesOfDay = (timeStr) => {
  if (!timeStr) return null;
  const m = String(timeStr).match(/^(\d{1,2}):(\d{2})/);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
export const overlaps = (aStart, aEnd, bStart, bEnd) => {
  if (aStart == null || aEnd == null || bStart == null || bEnd == null) return false;
  return aStart < bEnd && bStart < aEnd;
};

export const mondayOf = (d) => {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  const dow = (c.getDay() + 6) % 7; // Mon=0
  c.setDate(c.getDate() - dow);
  return c;
};
export const weekKey = (iso) => isoDate(mondayOf(parseISO(iso)));
export const weekLabel = (iso) => {
  const mon = mondayOf(parseISO(iso));
  return `Week of ${MONTHS[mon.getMonth()].slice(0, 3)} ${mon.getDate()}`;
};
export const groupByWeek = (items) => {
  const map = new Map();
  items.forEach((it) => {
    const k = weekKey(it.sessionDate);
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(it);
  });
  return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
};

export const fmtHrs = (n) => {
  if (n == null) return "—";
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
};

const isMine = heldByMe;

// Calendar pill look for the instructor's own sign-ups; everything else is mine (green) or open (amber).
const pillState = (o) => {
  if (isMine(o)) return { text: "You", fg: T.green, bg: T.green50 };
  if (o.mySignupStatus === "PENDING") return { text: o.status === "FILLED" ? "Filled" : "Requested", fg: T.blue, bg: T.blue50 };
  if (o.mySignupStatus === "UNAPPROVED") return { text: "Not approved", fg: T.danger, bg: T.danger50 };
  return { text: "Open", fg: T.amber, bg: T.amber50 };
};

// ── Icons ────────────────────────────────────────────────────────────────────
const Svg = ({ w = 14, h = 14, children, ...rest }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={w}
    height={h}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0, display: "block" }}
    {...rest}
  >
    {children}
  </svg>
);
const IconAgenda = () => (
  <Svg><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></Svg>
);
const IconCal = () => (
  <Svg><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></Svg>
);
const IconClock = () => (
  <Svg w={13} h={13}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></Svg>
);
const IconPin = () => (
  <Svg w={13} h={13}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /></Svg>
);
const IconChevL = () => (
  <Svg w={15} h={15} strokeWidth="2.5"><polyline points="15 18 9 12 15 6" /></Svg>
);
const IconChevR = () => (
  <Svg w={15} h={15} strokeWidth="2.5"><polyline points="9 18 15 12 9 6" /></Svg>
);
const IconX = () => (
  <Svg w={12} h={12} strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></Svg>
);

// ── Small pieces ─────────────────────────────────────────────────────────────
const SEL_SX = {
  height: 36, minWidth: 150, borderRadius: "9px", bgcolor: "#fff", fontSize: 13, fontWeight: 600, color: T.ink,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: T.line },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--color-hairline-cool)" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: T.primary, borderWidth: "1px" },
};

function Segmented({ value, options, onChange }) {
  return (
    <Box sx={{ display: "flex", gap: "2px", bgcolor: T.surface3, border: `1px solid ${T.line}`, borderRadius: "10px", p: "3px" }}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <Box
            key={o.value}
            component="button"
            type="button"
            onClick={() => onChange(o.value)}
            sx={{
              font: "inherit", fontSize: 12.5, fontWeight: 700, border: 0, cursor: "pointer",
              px: "13px", py: "7px", borderRadius: "7px", display: "inline-flex", alignItems: "center", gap: "6px",
              color: active ? T.ink : T.ink3,
              bgcolor: active ? "#fff" : "transparent",
              boxShadow: active ? T.shadowSm : "none",
            }}
          >
            {o.icon}
            {o.label}
            {o.count != null && (
              <Box component="span" sx={{ minWidth: 18, height: 18, px: "5px", borderRadius: "9px", fontSize: 11, display: "inline-grid", placeItems: "center",
                bgcolor: active ? T.primary : "var(--color-hairline-cloud)", color: active ? "#fff" : T.ink }}>
                {o.count}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

// ── My session card ──────────────────────────────────────────────────────────
function SlotCard({ opening, onAction }) {
  const d = parseISO(opening.sessionDate);
  const rm = ROLE_META[opening.teachingRole] || { label: opening.teachingRole };
  const locked = isDropLocked(opening);
  const daysLeft = Math.max(daysToSession(opening), 0);
  const topic = parseSessionTopics(opening.topics)[0] || opening.sessionName || null;
  const coStaff = Array.isArray(opening.coStaff) ? opening.coStaff : [];

  return (
    <Box
      data-testid={`slot-${opening.id}`}
      sx={{
        display: "grid", gridTemplateColumns: { xs: "52px minmax(0,1fr)", sm: "52px minmax(0,1fr) auto" }, gap: "18px",
        alignItems: "center", p: "16px 20px", bgcolor: "#fff", border: `1px solid ${T.line2}`,
        borderRadius: "16px", boxShadow: "var(--shadow-card-sm)", "& + &": { mt: "12px" },
      }}
    >
      <Box sx={{ textAlign: "center", lineHeight: 1.1 }}>
        <Box sx={{ fontSize: 11, fontWeight: 600, letterSpacing: ".5px", textTransform: "uppercase", color: "var(--color-accent-violet-mid)" }}>
          {MONTHS[d.getMonth()].slice(0, 3)}
        </Box>
        <Box sx={{ fontSize: 22, fontWeight: 700, my: "2px" }}>{d.getDate()}</Box>
        <Box sx={{ fontSize: 12, color: "var(--color-accent-violet-mid)" }}>{DOWS[d.getDay()]}</Box>
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: "10px", flexWrap: "wrap", mb: "2px" }}>
          <Box sx={{ fontSize: 16, fontWeight: 600 }}>{opening.courseTitle || opening.sessionName || "Session"}</Box>
          {opening.courseNumber && (
            <Box sx={{ fontSize: 12, color: "var(--color-accent-violet-mid)", fontFamily: "var(--font-mono)" }}>{opening.courseNumber}</Box>
          )}
        </Box>
        <FactLine sx={{ mb: "4px" }}>
          <span>{ROLE_LABEL[opening.teachingRole] || opening.teachingRole}</span>
          <Note tone="ok" icon={ICONS.check} sx={{ fontWeight: 600 }}>You</Note>
        </FactLine>
        {topic && <Box sx={{ fontSize: 13, color: "var(--color-ink-2)", mb: "4px" }}>{topic}</Box>}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "2px 16px", fontSize: 13, color: "var(--color-accent-violet-mid)" }}>
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <IconClock />
            {formatSessionTimeRange(opening)}
            {opening.durationHours != null && ` · ${fmtHrs(opening.durationHours)} hrs`}
          </Box>
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <IconPin />
            {opening.location || (opening.sessionType === "VIRTUAL" ? "Online" : "Location TBD")}
          </Box>
        </Box>
        {coStaff.length > 0 && (
          <Box sx={{ mt: "6px", fontSize: 12.5, color: "var(--color-accent-violet-mid)" }}>
            Co-staffed:{" "}
            <Box component="b" sx={{ color: T.ink, fontWeight: 600 }}>
              {coStaff.map((cs) => `${ROLE_SHORT[cs.teachingRole] || cs.teachingRole}: ${cs.instructorName || "open"}`).join(" · ")}
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", alignItems: { xs: "flex-start", sm: "flex-end" }, gap: "6px", gridColumn: { xs: 2, sm: "auto" } }}>
        <Button
          onClick={() => onAction({ kind: "drop", opening })}
          disabled={locked}
          sx={{
            height: 32, px: "12px", borderRadius: "10px", fontSize: 11, fontWeight: 600, letterSpacing: ".2px", textTransform: "uppercase",
            whiteSpace: "nowrap", bgcolor: "#fff", color: T.danger, border: "1px solid color-mix(in oklch, var(--color-danger) 30%, #fff)",
            "&:hover": { bgcolor: "var(--color-danger-bg)" },
            "&.Mui-disabled": { opacity: 0.4, color: T.danger },
          }}
        >
          Drop slot
        </Button>
        {locked ? (
          <Box sx={{ fontSize: 12, color: "var(--color-accent-violet-mid)", textAlign: { xs: "left", sm: "right" }, maxWidth: 220 }}>
            Locked — starts in {daysLeft} day{daysLeft === 1 ? "" : "s"} (2-day notice required)
          </Box>
        ) : rm.label === "Lead" || coStaff.length === 0 ? null : (
          <Box sx={{ fontSize: 12, color: "var(--color-accent-violet-mid)" }}>Drops this session only.</Box>
        )}
      </Box>
    </Box>
  );
}

// ── Month calendar ───────────────────────────────────────────────────────────
function CalendarView({ rows, conflicts, calMonth, onShiftMonth, onPickDay, onEvent }) {
  const y = calMonth.getFullYear();
  const m = calMonth.getMonth();
  const first = new Date(y, m, 1);
  const startOffset = first.getDay(); // Sunday-start
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7;

  const byDate = useMemo(() => {
    const map = new Map();
    rows.forEach((o) => {
      if (!map.has(o.sessionDate)) map.set(o.sessionDate, []);
      map.get(o.sessionDate).push(o);
    });
    return map;
  }, [rows]);

  let mineCount = 0;
  let openCount = 0;
  let conflictCount = 0;
  rows.forEach((o) => {
    if (!sameMonth(o.sessionDate, calMonth)) return;
    if (isMine(o)) mineCount += 1;
    else if (!isRequest(o)) openCount += 1;
    if (conflicts.has(o.id)) conflictCount += 1;
  });

  const cells = [];
  for (let i = 0; i < totalCells; i += 1) {
    const dayNum = i - startOffset + 1;
    const cellDate = new Date(y, m, dayNum);
    const iso = isoDate(cellDate);
    const muted = dayNum < 1 || dayNum > daysInMonth;
    const isToday = iso === isoDate(startOfToday());
    const entries = (byDate.get(iso) || []).slice().sort((a, b) => String(a.startTime || "").localeCompare(String(b.startTime || "")));
    const shown = entries.slice(0, 3);
    const extra = entries.length - shown.length;
    cells.push(
      <Box
        key={i}
        onClick={() => onPickDay(iso)}
        sx={{
          minHeight: 118, borderTop: `1px solid ${T.line2}`, borderRight: `1px solid ${T.line2}`,
          p: "7px", cursor: "pointer", display: "flex", flexDirection: "column",
          bgcolor: muted ? T.surface2 : "#fff",
          "&:nth-of-type(7n+1)": { borderLeft: 0 },
          "&:hover": { bgcolor: T.surface2 },
        }}
      >
        <Box
          sx={{
            fontSize: 12.5, fontWeight: 700, color: muted ? T.ink4 : "var(--color-ink-2)", flexShrink: 0,
            ...(isToday && { bgcolor: T.primary, color: "#fff", borderRadius: "50%", width: 20, height: 20, display: "inline-flex", alignItems: "center", justifyContent: "center" }),
          }}
        >
          {cellDate.getDate()}
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "3px", mt: "5px", minWidth: 0 }}>
          {shown.map((o) => {
            const pill = pillState(o);
            return (
              <Box
                key={o.id}
                component="button"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onEvent(o);
                }}
                sx={{
                  border: 0, textAlign: "left", width: "100%", p: "3px 6px", borderRadius: "5px", font: "inherit",
                  fontSize: 10.5, fontWeight: 700, lineHeight: 1.35, cursor: "pointer",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  bgcolor: pill.bg,
                  color: pill.fg,
                  boxShadow: conflicts.has(o.id) ? `inset 0 0 0 1.5px ${T.danger}` : "none",
                }}
              >
                {o.courseNumber || "Session"} · {pill.text} — {ROLE_META[o.teachingRole]?.label || o.teachingRole}
              </Box>
            );
          })}
          {extra > 0 && (
            <Box
              component="button"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onPickDay(iso);
              }}
              sx={{ border: 0, bgcolor: "transparent", p: "2px 6px", font: "inherit", fontSize: 10.5, fontWeight: 700, color: T.ink3, textAlign: "left", cursor: "pointer", "&:hover": { color: T.primary, textDecoration: "underline" } }}
            >
              +{extra} more
            </Box>
          )}
        </Box>
      </Box>,
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "14px" }}>
        <Typography sx={{ fontSize: 18, fontWeight: 700, fontFamily: T.fontDisplay }}>
          {MONTHS[m]} {y}
        </Typography>
        <Box sx={{ display: "flex", gap: "6px" }}>
          {[["prev", -1, "Previous month", <IconChevL key="l" />], ["next", 1, "Next month", <IconChevR key="r" />]].map(([k, delta, label, ic]) => (
            <Box
              key={k}
              component="button"
              type="button"
              aria-label={label}
              onClick={() => onShiftMonth(delta)}
              sx={{ width: 32, height: 32, borderRadius: "8px", border: `1px solid ${T.line}`, bgcolor: "#fff", cursor: "pointer", display: "grid", placeItems: "center", color: "var(--color-ink-2)", "&:hover": { bgcolor: T.surface2 } }}
            >
              {ic}
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ fontSize: 13, color: T.ink3, mb: "12px" }}>
        <b>{mineCount}</b> session{mineCount === 1 ? "" : "s"} assigned to you · <b>{openCount}</b> open slot{openCount === 1 ? "" : "s"} you qualify for
        {conflictCount > 0 && (
          <>
            {" · "}
            <Box component="b" sx={{ color: T.danger }}>{conflictCount}</Box> with a time conflict
          </>
        )}{" "}
        this month
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", border: `1px solid ${T.line}`, borderRadius: "14px", overflow: "hidden" }}>
        {DOWS.map((d) => (
          <Box key={d} sx={{ bgcolor: T.surface3, fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: T.ink3, textAlign: "center", py: "9px", letterSpacing: ".05em" }}>
            {d}
          </Box>
        ))}
        {cells}
      </Box>

      <Box sx={{ display: "flex", gap: "18px", mt: "14px", fontSize: 12.5, color: T.ink3, flexWrap: "wrap" }}>
        {[
          ["You're teaching", T.green50, T.green],
          ["Open slot you qualify for", T.amber50, T.amber],
          ["Requested, waiting on a coordinator", T.blue50, T.blue],
          ["Request not approved", T.danger50, T.danger],
          ["Time conflict with your schedule", "#fff", T.danger],
        ].map(([label, bg, ring]) => (
          <Box key={label} sx={{ display: "inline-flex", alignItems: "center", gap: "7px" }}>
            <Box sx={{ width: 10, height: 10, borderRadius: "3px", bgcolor: bg, boxShadow: `inset 0 0 0 1px ${ring}` }} />
            {label}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function InstructorSchedulingPage() {
  const { tenantId, user } = useActiveTenant();
  const userId = user?.id;

  const [raw, setRaw] = useState([]);
  const [noTeachingRoles, setNoTeachingRoles] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState("agenda");
  const [filters, setFilters] = useState({ who: "all", role: "ANY", courseId: "ANY", range: "30", day: null });
  const [calMonth, setCalMonth] = useState(startOfMonth(new Date()));
  const [confirm, setConfirm] = useState(null); // { kind, opening }
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null); // { severity, message }
  const [details, setDetails] = useState(null); // { classId, courseTitle, ..., sessionIds }

  const load = useCallback(() => {
    if (!tenantId) return Promise.resolve();
    const from = startOfMonth(new Date());
    const to = addDays(new Date(), 400);
    setLoading(true);
    return fetchMyOpenings(tenantId, isoDate(from), isoDate(to))
      .then((data) => {
        setRaw(Array.isArray(data) ? data : []);
        setError(null);
      })
      .catch(() => setError("Could not load your schedule."))
      .finally(() => setLoading(false));
  }, [tenantId]);

  // Without a granted teaching role the Open Slots tab is always empty; say why instead of leaving it blank.
  useEffect(() => {
    if (!tenantId || userId == null) return;
    Promise.resolve(fetchMyTeachingRoles(userId, tenantId))
      .then((d) => setNoTeachingRoles(Array.isArray(d?.teachingRoles) && d.teachingRoles.length === 0))
      .catch(() => setNoTeachingRoles(false));
  }, [tenantId, userId]);

  useEffect(() => {
    load();
  }, [load]);

  // ── derived ──
  const conflicts = useMemo(() => {
    const mineByDate = new Map();
    raw.filter(isMine).forEach((o) => {
      if (!mineByDate.has(o.sessionDate)) mineByDate.set(o.sessionDate, []);
      mineByDate.get(o.sessionDate).push(o);
    });
    const set = new Set();
    raw.filter((o) => !isMine(o)).forEach((o) => {
      const mine = mineByDate.get(o.sessionDate) || [];
      const aS = minutesOfDay(o.startTime);
      const aE = minutesOfDay(o.endTime);
      if (mine.some((mm) => overlaps(aS, aE, minutesOfDay(mm.startTime), minutesOfDay(mm.endTime)))) {
        set.add(o.id);
      }
    });
    return set;
  }, [raw]);

  const courseOptions = useMemo(() => {
    const seen = new Map();
    raw.forEach((o) => {
      if (o.classId == null) return;
      const id = String(o.classId);
      if (seen.has(id)) return;
      const label = o.courseNumber
        ? `${o.courseNumber} — ${o.courseTitle || o.sessionName || ""}`.trim()
        : o.courseTitle || o.sessionName || `Class ${id}`;
      seen.set(id, label);
    });
    return [...seen.entries()].map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [raw]);

  // Everything the filters allow; the tabs below decide which sections show.
  const visible = useMemo(() => {
    const rangeDays = filters.range === "all" ? Infinity : Number(filters.range);
    return raw
      .filter((o) => {
        const d = daysToSession(o);
        if (d < 0) return false;
        if (filters.role !== "ANY" && o.teachingRole !== filters.role) return false;
        if (filters.courseId !== "ANY" && String(o.classId) !== filters.courseId) return false;
        if (filters.day) return o.sessionDate === filters.day;
        return d <= rangeDays;
      })
      .sort((a, b) => a.sessionDate.localeCompare(b.sessionDate) || String(a.startTime || "").localeCompare(String(b.startTime || "")));
  }, [raw, filters]);

  const mineItems = useMemo(() => visible.filter(isMine), [visible]);
  const openGroups = useMemo(() => groupByCourse(groupOpenSlots(visible)), [visible]);
  const requestGroups = useMemo(() => groupRequests(visible), [visible]);
  const pendingCount = requestGroups.filter((r) => r.status === "PENDING" && !r.lost).length;
  const weeks = useMemo(() => groupByWeek(mineItems), [mineItems]);

  // ── actions ──
  const submitConfirm = async () => {
    if (!confirm) return;
    const { kind, opening } = confirm;
    const targets = confirm.openings || [opening];
    const ids = new Set(targets.map((t) => t.id));
    const snapshot = raw;
    setSubmitting(true);
    setRaw((rs) => rs.map((r) => (!ids.has(r.id) ? r : { ...r, ...optimisticPatch(kind, r) })));
    setConfirm(null);
    try {
      for (const t of targets) {
        // eslint-disable-next-line no-await-in-loop
        await runSlotAction(kind, t, tenantId);
      }
      setToast({ severity: "success", message: SUCCESS_MESSAGE[kind] });
      await load();
    } catch (e) {
      setRaw(snapshot);
      setToast({ severity: "error", message: friendlySlotError(e, kind) });
      await load();
    } finally {
      setSubmitting(false);
    }
  };

  // A multi-session request: one call per session, so some can succeed while others are refused.
  const submitBulk = async (kind, openings) => {
    setSubmitting(true);
    try {
      if (kind === "signup") {
        // One call per class, so the coordinators get one email for the whole request instead of one per session.
        const outcomes = await runSignupBatch(openings, tenantId);
        const refused = outcomes.filter((r) => r.status !== "CREATED");
        const sent = outcomes.length - refused.length;
        setToast(refused.length === 0
          ? { severity: "success", message: `${sent} request${sent === 1 ? "" : "s"} sent — pending coordinator approval.` }
          : {
            severity: sent ? "warning" : "error",
            message: `${sent} of ${outcomes.length} sent. ${refused[0].reason || "Some sessions could not be requested."}${sent ? " The rest are still listed so you can try them again." : ""}`,
          });
        await load();
        return;
      }
      const results = await Promise.allSettled(openings.map((o) => runSlotAction(kind, o, tenantId)));
      const failed = results.filter((r) => r.status === "rejected");
      const ok = results.length - failed.length;
      if (failed.length === 0) {
        setToast({
          severity: "success",
          message: kind === "signup"
            ? `${ok} request${ok === 1 ? "" : "s"} sent — pending coordinator approval.`
            : `${ok} slot${ok === 1 ? "" : "s"} requested — you're now assigned.`,
        });
      } else {
        setToast({
          severity: ok ? "warning" : "error",
          message: `${ok} of ${results.length} sent. ${friendlySlotError(failed[0].reason, kind)}${ok ? " The rest are still listed so you can try them again." : ""}`,
        });
      }
      await load();
    } finally {
      setSubmitting(false);
    }
  };

  if (!tenantId) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <NoTenantSelected message="Select a training center to see your teaching schedule." />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1180, mx: "auto", p: { xs: "20px 18px 80px", sm: "28px 36px 100px" } }}>
      {/* head */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "20px", flexWrap: "wrap", mb: "24px" }}>
        <Box>
          <Typography sx={{ fontSize: 30, fontWeight: 800, letterSpacing: "-.03em", color: T.ink, fontFamily: T.fontDisplay }}>
            My Teaching Schedule
          </Typography>
          <Typography sx={{ mt: "6px", fontSize: 14, color: T.ink3, fontWeight: 500 }}>
            See your assigned sessions and claim open slots you&apos;re qualified for.
          </Typography>
        </Box>
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { value: "agenda", label: "Agenda", icon: <IconAgenda /> },
            { value: "calendar", label: "Calendar", icon: <IconCal /> },
          ]}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      ) : view === "agenda" ? (
        <>
          {/* filter bar */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", mb: "20px", pb: "18px", borderBottom: `1px solid ${T.line}` }}>
            <Segmented
              value={filters.who}
              onChange={(who) => setFilters((f) => ({ ...f, who }))}
              options={[
                { value: "all", label: "All" },
                { value: "mine", label: "My Sessions", count: mineItems.length },
                { value: "open", label: "Open Slots", count: openGroups.length },
                { value: "req", label: "My Requests", count: pendingCount },
              ]}
            />
            <Select size="small" value={filters.role} onChange={(e) => setFilters((f) => ({ ...f, role: e.target.value }))} sx={SEL_SX}>
              <MenuItem value="ANY">Any role</MenuItem>
              <MenuItem value="LEAD_INSTRUCTOR">Lead Instructor</MenuItem>
              <MenuItem value="ASSISTANT_INSTRUCTOR">Assistant Instructor</MenuItem>
              <MenuItem value="TA">TA</MenuItem>
            </Select>
            <Select size="small" value={filters.courseId} onChange={(e) => setFilters((f) => ({ ...f, courseId: e.target.value }))} sx={SEL_SX}>
              <MenuItem value="ANY">All courses</MenuItem>
              {courseOptions.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>
              ))}
            </Select>
            <Select size="small" value={filters.range} onChange={(e) => setFilters((f) => ({ ...f, range: e.target.value }))} sx={{ ...SEL_SX, minWidth: 130 }}>
              <MenuItem value="7">Next 7 days</MenuItem>
              <MenuItem value="30">Next 30 days</MenuItem>
              <MenuItem value="60">Next 60 days</MenuItem>
              <MenuItem value="90">Next 90 days</MenuItem>
              <MenuItem value="all">All upcoming</MenuItem>
            </Select>
            {filters.day && (
              <Box
                component="button"
                type="button"
                onClick={() => setFilters((f) => ({ ...f, day: null }))}
                sx={{ display: "inline-flex", alignItems: "center", gap: "6px", height: 36, px: "12px", borderRadius: "9px", bgcolor: T.primaryTint, color: T.primary, fontSize: 12.5, fontWeight: 700, border: `1px solid ${T.line}`, cursor: "pointer", font: "inherit" }}
              >
                <IconX />
                {MONTHS[parseISO(filters.day).getMonth()].slice(0, 3)} {parseISO(filters.day).getDate()}
              </Box>
            )}
          </Box>

          {noTeachingRoles && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: "10px" }}>
              You haven't been granted any teaching roles yet, so you can't claim open slots. Ask a coordinator to
              grant you a role (Instructor Management → Teaching Roles).
            </Alert>
          )}
          {(() => {
            const showOpen = filters.who === "all" || filters.who === "open";
            const showReq = filters.who === "all" || filters.who === "req";
            const showMine = filters.who === "all" || filters.who === "mine";
            const sectionHead = (title, sub) => (
              <Box sx={{ display: "flex", alignItems: "baseline", gap: "12px", flexWrap: "wrap", mb: "12px" }}>
                <Typography component="h2" sx={{ m: 0, fontSize: 18, fontWeight: 700 }}>{title}</Typography>
                {sub && <Typography sx={{ m: 0, fontSize: 13, color: "var(--color-accent-violet-mid)" }}>{sub}</Typography>}
              </Box>
            );
            const empty = (text) => (
              <Box sx={{ p: "28px 20px", textAlign: "center", color: "var(--color-accent-violet-mid)", fontSize: 13.5, bgcolor: "#fff", border: `1px solid ${T.line2}`, borderRadius: "16px" }}>
                {text}
              </Box>
            );
            return (
              <>
                {showOpen && (
                  <Box component="section" sx={{ mt: "32px" }}>
                    {sectionHead("Open slots", "Request a whole class, or pick the sessions that fit your schedule.")}
                    {openGroups.length === 0
                      ? empty("No open slots match your filters.")
                      : openGroups.map((g, i) => (
                        <OpenClassCard key={g.key} course={g} defaultOpen={i === 0} conflicts={conflicts} busy={submitting} onAct={setConfirm} onBulk={submitBulk} onDetails={setDetails} />
                      ))}
                  </Box>
                )}
                {showReq && (
                  <Box component="section" sx={{ mt: "32px" }}>
                    {sectionHead("My requests", "Waiting on a coordinator, or recently decided.")}
                    {requestGroups.length === 0
                      ? empty("You have no requests.")
                      : (
                        <Box sx={{ bgcolor: "#fff", border: `1px solid ${T.line2}`, borderRadius: "16px", boxShadow: T.shadowSm }}>
                          {requestGroups.map((r) => (
                            <RequestRow key={r.key} request={r} busy={submitting} onDetails={setDetails}
                              onWithdraw={(req) => setConfirm({ kind: "withdraw", opening: req.openings[0], openings: req.openings })} />
                          ))}
                        </Box>
                      )}
                  </Box>
                )}
                {showMine && (
                  <Box component="section" sx={{ mt: "32px" }}>
                    {sectionHead("My sessions")}
                    {weeks.length === 0 ? empty("No sessions assigned to you match these filters.") : weeks.map(([wk, items]) => (
                      <Box key={wk}>
                        <Box sx={{ m: "22px 0 10px", font: "600 12px var(--font-ui)", letterSpacing: ".6px", textTransform: "uppercase", color: "var(--color-accent-violet-mid)" }}>
                          {weekLabel(items[0].sessionDate)}
                        </Box>
                        {items.map((o) => <SlotCard key={o.id} opening={o} onAction={setConfirm} />)}
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            );
          })()}
        </>
      ) : (
        <CalendarView
          rows={raw}
          conflicts={conflicts}
          calMonth={calMonth}
          onShiftMonth={(delta) => setCalMonth((d) => new Date(d.getFullYear(), d.getMonth() + delta, 1))}
          onPickDay={(iso) => {
            setFilters((f) => ({ ...f, day: iso }));
            setView("agenda");
          }}
          onEvent={(o) => {
            if (isMine(o) && isDropLocked(o)) return;
            const kind = slotActionKind(o);
            if (kind) setConfirm({ kind, opening: o });
          }}
        />
      )}

      <ClassDetailsDialog target={details} tenantId={tenantId} onClose={() => setDetails(null)} />

      {/* confirm dialog */}
      <Dialog open={Boolean(confirm)} onClose={() => !submitting && setConfirm(null)} maxWidth="xs" fullWidth>
        {confirm && (
          <>
            <DialogTitle sx={{ fontWeight: 700, fontFamily: T.fontDisplay }}>
              {CONFIRM_TITLE[confirm.kind]}
            </DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ fontSize: 13.5, color: "var(--color-ink-2)" }}>
                {confirm.kind === "signup" || confirm.kind === "withdraw" ? (
                  <>
                    {confirm.kind === "signup"
                      ? "Your sign-up will be Pending Approval until a coordinator approves it. You won't be assigned until then."
                      : (confirm.openings?.length || 1) > 1
                        ? `Your ${confirm.openings.length} pending sign-ups for this class will be removed.`
                        : "Your pending sign-up will be removed."}{" "}
                    <b>{ROLE_LABEL[confirm.opening.teachingRole] || confirm.opening.teachingRole}</b> ·{" "}
                    <b>{confirm.opening.courseTitle || confirm.opening.sessionName || "this session"}</b>
                    {confirm.opening.courseNumber ? ` (${confirm.opening.courseNumber})` : ""},{" "}
                    {MONTHS[parseISO(confirm.opening.sessionDate).getMonth()]} {parseISO(confirm.opening.sessionDate).getDate()}
                    , {formatSessionTimeRange(confirm.opening)}.
                  </>
                ) : confirm.kind === "request" ? (
                  <>
                    You&apos;ll be added as <b>{ROLE_LABEL[confirm.opening.teachingRole] || confirm.opening.teachingRole}</b> for{" "}
                    <b>{confirm.opening.courseTitle || confirm.opening.sessionName || "this session"}</b>
                    {confirm.opening.courseNumber ? ` (${confirm.opening.courseNumber})` : ""} on{" "}
                    <b>
                      {MONTHS[parseISO(confirm.opening.sessionDate).getMonth()]} {parseISO(confirm.opening.sessionDate).getDate()}
                    </b>
                    , {formatSessionTimeRange(confirm.opening)}. You&apos;ll be responsible for delivering this session.
                  </>
                ) : (
                  <>
                    This releases your <b>{ROLE_LABEL[confirm.opening.teachingRole] || confirm.opening.teachingRole}</b> assignment on{" "}
                    <b>{confirm.opening.courseTitle || confirm.opening.sessionName || "this session"}</b>
                    {confirm.opening.courseNumber ? ` (${confirm.opening.courseNumber})` : ""},{" "}
                    {MONTHS[parseISO(confirm.opening.sessionDate).getMonth()]} {parseISO(confirm.opening.sessionDate).getDate()}. The slot reopens
                    immediately for other qualified instructors. You can&apos;t drop within 2 days of the session.
                  </>
                )}
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 2, pb: 2 }}>
              <Button onClick={() => setConfirm(null)} disabled={submitting} sx={{ textTransform: "none" }}>
                Cancel
              </Button>
              <Button
                onClick={submitConfirm}
                disabled={submitting}
                variant="contained"
                color={confirm.kind === "drop" ? "error" : "primary"}
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: "8px", ...(confirm.kind !== "drop" && { bgcolor: T.primary }) }}
              >
                {CONFIRM_BUTTON[confirm.kind]}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)} sx={{ borderRadius: "12px" }}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
